import { useState } from 'react';

export type UseScriptStatus = 'idle' | 'loading' | 'ready' | 'error';
export interface UseScriptOptions {
  shouldPreventLoad?: boolean;
  removeOnUnmount?: boolean;
}

const cachedScriptStatuses: Record<string, UseScriptStatus | undefined> = {};

function getScriptNode(src: string) {
  const node: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`);
  const status = node?.getAttribute('data-status') as UseScriptStatus | undefined;

  return {
    node,
    status,
  };
}

const createScriptNode = (src: string) => {
  const scriptNode = document.createElement('script');
  scriptNode.src = src;
  scriptNode.async = true;
  scriptNode.setAttribute('data-status', 'loading');
  document.body.appendChild(scriptNode);

  const setAttributeFromEvent = (event: Event) => {
    const scriptStatus: UseScriptStatus = event.type === 'load' ? 'ready' : 'error';

    scriptNode?.setAttribute('data-status', scriptStatus);
  };

  scriptNode.addEventListener('load', setAttributeFromEvent);
  scriptNode.addEventListener('error', setAttributeFromEvent);

  return scriptNode;
};

export function useScript(options?: UseScriptOptions) {
  const [status, setStatus] = useState<UseScriptStatus>('idle');

  const loadScript = (src: string) => {
    if (!src || options?.shouldPreventLoad) {
      return;
    }

    const cachedScriptStatus = cachedScriptStatuses[src];
    if (cachedScriptStatus === 'ready' || cachedScriptStatus === 'error') {
      setStatus(cachedScriptStatus);
      return;
    }

    const scriptNode = getOrCreateScriptNode(src, cachedScriptStatus);
    const setStateFromEvent = createSetStateFromEvent(src);

    scriptNode.addEventListener('load', setStateFromEvent);
    scriptNode.addEventListener('error', setStateFromEvent);

    return createCleanupFunction(scriptNode, src);
  };

  const getOrCreateScriptNode = (src: string, cachedScriptStatus?: UseScriptStatus) => {
    const script = getScriptNode(src);
    let scriptNode = script.node;

    if (!scriptNode) {
      scriptNode = createScriptNode(src);
    } else {
      setStatus(script.status ?? cachedScriptStatus ?? 'loading');
    }

    return scriptNode;
  };

  const createSetStateFromEvent = (src: string) => (event: Event) => {
    const newStatus = event.type === 'load' ? 'ready' : 'error';
    setStatus(newStatus);
    cachedScriptStatuses[src] = newStatus;
  };

  const createCleanupFunction = (scriptNode: HTMLScriptElement, src: string) => () => {
    if (scriptNode) {
      scriptNode.removeEventListener('load', createSetStateFromEvent(src));
      scriptNode.removeEventListener('error', createSetStateFromEvent(src));
    }

    if (scriptNode && options?.removeOnUnmount) {
      scriptNode.remove();
    }
  };

  return { status, loadScript };
}
